import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { getSectionsByTab, type WizardSection } from "@shared/section-definitions";
import { useState } from "react";
import { HelpCircle, ClipboardList } from "lucide-react";
import { PreparationChecklistSheet } from "@/components/workflow/common/PreparationChecklistSheet";

interface WizardSectionNavProps {
  currentTab: "general" | "environment" | "social" | "governance" | "sustainability-story";
  currentSectionId: string;
  onSectionClick: (section: WizardSection) => void;
  sectionCompletions?: Record<string, number>; // cardKey -> completion percentage
  className?: string;
}

export function WizardSectionNav({
  currentTab,
  currentSectionId,
  onSectionClick,
  sectionCompletions = {},
  className,
}: WizardSectionNavProps) {
  const sections = getSectionsByTab(currentTab);
  const [isChecklistOpen, setIsChecklistOpen] = useState(false);

  return (
    <nav className={cn("flex flex-col h-full", className)}>
      {/* Header */}
      <div className="pb-2 px-3">
        <span className="text-slate-400 text-xs font-bold uppercase leading-4 tracking-wider">
          Sections
        </span>
      </div>

      {/* Section Items + Support Card (scrollable together) */}
      <ScrollArea className="flex-1 min-h-0 [&_[data-radix-scroll-area-viewport]>div]:!flex [&_[data-radix-scroll-area-viewport]>div]:!flex-col [&_[data-radix-scroll-area-viewport]>div]:!min-h-full">
        <div className="space-y-1">
          {sections.map((section) => {
            const isActive = section.id === currentSectionId;
            const SectionIcon = section.icon;

            return (
              <button
                key={section.id}
                onClick={() => onSectionClick(section)}
                className={cn(
                  "w-full flex items-center gap-3 pl-3 pr-5 py-2.5 rounded-2xl text-left transition-colors",
                  "hover:bg-slate-500/5",
                  isActive ? "bg-slate-500/10" : ""
                )}
              >
                <SectionIcon
                  className={cn(
                    "h-4 w-4 flex-shrink-0",
                    isActive ? "text-primary" : "text-slate-500"
                  )}
                />
                <span
                  className={cn(
                    "text-sm leading-5 truncate",
                    isActive
                      ? "text-primary font-bold"
                      : "text-slate-600 font-normal"
                  )}
                >
                  {section.displayName}
                </span>
              </button>
            );
          })}
        </div>

        {/* Support Card */}
        <div className="mt-auto pt-4 p-4 bg-slate-50 rounded-3xl border border-slate-200 flex flex-col gap-2">
          <span className="text-slate-500 text-xs font-semibold uppercase leading-4 tracking-wide">
            Support
          </span>
          <a
            href="/knowledge-base"
            className="inline-flex items-center gap-2 text-slate-600 text-sm font-normal hover:text-slate-800 transition-colors"
          >
            <HelpCircle className="h-4 w-4 flex-shrink-0 text-slate-500" />
            <span>Knowledge Base</span>
          </a>
          <button
            type="button"
            onClick={() => setIsChecklistOpen(true)}
            className="cursor-pointer inline-flex items-center gap-2 text-slate-600 text-sm font-normal hover:text-slate-800 transition-colors text-left"
          >
            <ClipboardList className="h-4 w-4 flex-shrink-0 text-slate-500" />
            <span>Preparation Checklist</span>
          </button>
        </div>
      </ScrollArea>
      <PreparationChecklistSheet
        open={isChecklistOpen}
        onOpenChange={setIsChecklistOpen}
      />
    </nav>
  );
}
